/**
 * BlogPagination — offset-based pagination control for the blog index +
 * category + tag pages. Renders Prev / Next controls + a current-page
 * indicator.
 *
 * The control is intentionally minimal — visitors needing fine-grained
 * jumping use the marketing-website's search or the per-category drill-
 * down. Page numbers are 1-indexed for display.
 *
 * WS-J — Prev/Next are real `<a href>` anchors, not buttons. They were buttons
 * with no URL, which made every page past the first uncrawlable and
 * undeep-linkable: on parcel that was ~35 pages / ~400 posts reachable only by
 * clicking Next, while the index's `CollectionPage` JSON-LD advertised 417 items
 * whose list pages had no address. `onClick` still drives client-side paging
 * (preventDefault + the caller's handler), so the SPA feel is unchanged — the
 * href exists for crawlers, middle-click, copy-link, and Back/Forward.
 *
 * The end states stay `<button disabled>` rather than becoming inert anchors: an
 * `<a>` with no href is not focusable and announces nothing useful, whereas a
 * disabled button is the correct accessible affordance for "there is no next
 * page".
 *
 * @module @puffin-core/marketing-shell/blog/BlogPagination
 */

import PropTypes from 'prop-types';

import { useMarketingShellTranslate } from '../hooks/useMarketingShellTranslate.js';

/**
 * @param {Object} props
 * @param {number} props.currentOffset - the first post's index on this page
 * @param {number} props.pageSize
 * @param {number} [props.totalKnownCount] - the total, when the caller knows it ("Page 2 of 5")
 * @param {boolean} props.hasMore - whether a next page exists
 * @param {() => void} props.onPrev
 * @param {() => void} props.onNext
 * @param {(page: number) => string} props.hrefForPage - a 1-indexed page's URL
 * @param {string} [props.prevLabel] - the caller's word; else the catalog's `blogPagination.previous`
 * @param {string} [props.nextLabel] - the caller's word; else the catalog's `blogPagination.next`
 * @param {string} [props.ariaLabel] - the caller's word; else the catalog's `blogPagination.label`
 * @returns {React.JSX.Element}
 */
export default function BlogPagination({
  currentOffset,
  pageSize,
  totalKnownCount,
  hasMore,
  onPrev,
  onNext,
  // WS-J — REQUIRED. Maps a 1-indexed page to its URL. Injected rather than
  // computed here because this control is shared by the index, category and tag
  // pages, whose URL grammars differ (`/blog/page/2` vs
  // `/blog/category/<slug>/page/2`). Baking the index's shape into the shared
  // component is exactly the caller-specific coupling this package forbids.
  hrefForPage,
  prevLabel: prevLabelProp,
  nextLabel: nextLabelProp,
  ariaLabel: ariaLabelProp,
}) {
  // PACKAGE-I18N-SEAT: a caller's label wins; without one the words come from the catalog, so a
  // Spanish page no longer reads "Previous" / "Page 2 of 5".
  const t = useMarketingShellTranslate();
  const prevLabel = prevLabelProp ?? t('blogPagination.previous');
  const nextLabel = nextLabelProp ?? t('blogPagination.next');
  const ariaLabel = ariaLabelProp ?? t('blogPagination.label');
  const currentPage = Math.floor(currentOffset / pageSize) + 1;
  const totalPages =
    typeof totalKnownCount === 'number' && totalKnownCount > 0
      ? Math.max(1, Math.ceil(totalKnownCount / pageSize))
      : null;
  const isOnFirst = currentOffset <= 0;
  const isOnLast = !hasMore;

  // A plain-left-click with no modifier is ours to handle; anything else
  // (ctrl/cmd/shift/alt, middle-click) must fall through to the browser so
  // "open in new tab" keeps working — the main reason to ship a real href.
  const handleActivate = (handler) => (event) => {
    if (event.defaultPrevented) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    handler();
  };

  return (
    <nav className="post-pagination" aria-label={ariaLabel}>
      {isOnFirst ? (
        <button
          type="button"
          className="btn btn-marketing-secondary"
          disabled
          aria-label={prevLabel}
        >
          {prevLabel}
        </button>
      ) : (
        <a
          className="btn btn-marketing-secondary"
          href={hrefForPage(currentPage - 1)}
          rel="prev"
          onClick={handleActivate(onPrev)}
          aria-label={prevLabel}
        >
          {prevLabel}
        </a>
      )}
      <span className="post-pagination-status" aria-live="polite">
        {totalPages
          ? t('blogPagination.pageOf', { page: currentPage, total: totalPages })
          : t('blogPagination.page', { page: currentPage })}
      </span>
      {isOnLast ? (
        <button
          type="button"
          className="btn btn-marketing-secondary"
          disabled
          aria-label={nextLabel}
        >
          {nextLabel}
        </button>
      ) : (
        <a
          className="btn btn-marketing-secondary"
          href={hrefForPage(currentPage + 1)}
          rel="next"
          onClick={handleActivate(onNext)}
          aria-label={nextLabel}
        >
          {nextLabel}
        </a>
      )}
    </nav>
  );
}

BlogPagination.propTypes = {
  currentOffset: PropTypes.number.isRequired,
  pageSize: PropTypes.number.isRequired,
  totalKnownCount: PropTypes.number,
  hasMore: PropTypes.bool.isRequired,
  onPrev: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  hrefForPage: PropTypes.func.isRequired,
  prevLabel: PropTypes.string,
  nextLabel: PropTypes.string,
  ariaLabel: PropTypes.string,
};
